export type CampaignPricingSku = {
  id: string;
  label: string;
  productType: string;
  analysesCount: number;
  amountCents: number;
  description: string;
  features: readonly string[];
  badge?: string;
};

export type CampaignPricingService = {
  id: 'feature' | 'tv' | 'theater';
  label: string;
  headline: string;
  blurb: string;
  scope: string;
  skus: readonly CampaignPricingSku[];
};

const FULL_REPORT_FEATURES = [
  'Complete logline & synopsis',
  'Character breakdown & analysis',
  'Structure & pacing evaluation',
  'Market & audience positioning',
  'Industry-grade scoring',
  'Delivered in under an hour',
] as const;

export const FEATURE_FILM_PACKS: readonly CampaignPricingSku[] = [
  {
    id: 'feature-film-1',
    label: '1 analysis',
    productType: 'feature_film_screenplay',
    analysesCount: 1,
    amountCents: 4999,
    description: 'Full professional coverage for a single feature screenplay.',
    features: ['1 full coverage report', ...FULL_REPORT_FEATURES],
  },
  {
    id: 'feature-film-3',
    label: '3 analyses',
    productType: 'feature_film_screenplay',
    analysesCount: 3,
    amountCents: 13499,
    description: 'Coverage for three feature screenplays at a discounted rate.',
    features: ['3 full coverage reports', 'Save 10% per analysis', ...FULL_REPORT_FEATURES],
  },
  {
    id: 'feature-film-5',
    label: '5 analyses',
    productType: 'feature_film_screenplay',
    analysesCount: 5,
    amountCents: 19999,
    description: 'Our best value for serious writers with multiple projects.',
    features: ['5 full coverage reports', 'Save 20% per analysis', ...FULL_REPORT_FEATURES],
    badge: 'Best value',
  },
] as const;

export const TV_SERIES_PACKS: readonly CampaignPricingSku[] = [
  {
    id: 'tv-series-1',
    label: '1 analysis',
    productType: 'tv_series',
    analysesCount: 1,
    amountCents: 3999,
    description: 'Full professional coverage for a single TV pilot & bible.',
    features: ['1 full coverage report', ...FULL_REPORT_FEATURES],
  },
  {
    id: 'tv-series-3',
    label: '3 analyses',
    productType: 'tv_series',
    analysesCount: 3,
    amountCents: 10799,
    description: 'Coverage for three TV pilots at a discounted rate.',
    features: ['3 full coverage reports', 'Save 10% per analysis', ...FULL_REPORT_FEATURES],
  },
  {
    id: 'tv-series-5',
    label: '5 analyses',
    productType: 'tv_series',
    analysesCount: 5,
    amountCents: 15999,
    description: 'Our best value for showrunners and staffing-season prep.',
    features: ['5 full coverage reports', 'Save 20% per analysis', ...FULL_REPORT_FEATURES],
    badge: 'Best value',
  },
] as const;

export const ADDITIONAL_OFFERING_SKUS: readonly CampaignPricingSku[] = [
  {
    id: 'novel-adaptation',
    label: 'Novel Adaptation',
    productType: 'novel_adaptation',
    analysesCount: 1,
    amountCents: 4999,
    description:
      'Book-to-screen adaptation intelligence — source material strengths, adaptation strategy, and market angle.',
    features: ['Source-material strengths', 'Adaptation strategy', 'Market angle'],
  },
  {
    id: 'pitch-deck',
    label: 'Pitch Deck Analysis',
    productType: 'pitch_deck',
    analysesCount: 1,
    amountCents: 999,
    description: 'Investor and buyer deck review.',
    features: ['Deck structure notes', 'Buyer-facing positioning'],
  },
] as const;

export const PRICING_CAMPAIGN_SERVICES: readonly CampaignPricingService[] = [
  {
    id: 'feature',
    label: 'Feature Films',
    headline: 'Feature Film Coverage',
    blurb:
      'Coverage for feature-length screenplays, from early structure to a complete professional read.',
    scope: 'Feature screenplays up to 140 pages',
    skus: [
      {
        id: 'feature-outline',
        label: 'Outline',
        productType: 'feature_film_outline',
        analysesCount: 1,
        amountCents: 999,
        description: 'Beat-sheet and outline review for early-stage development.',
        features: ['Structure notes & beat check', 'Concept viability feedback'],
      },
      {
        id: 'feature-writer',
        label: "Writer's Coverage",
        productType: 'feature_film_writer_coverage',
        analysesCount: 1,
        amountCents: 1999,
        description: 'Story, structure, character, dialogue, and originality feedback.',
        features: [
          'Writer-focused story notes',
          'No producer or executive analysis',
          'Delivered in under an hour',
        ],
      },
      {
        id: 'feature-full',
        label: 'Full Professional Coverage',
        productType: 'feature_film_screenplay',
        analysesCount: 1,
        amountCents: 4999,
        description: 'Complete professional coverage for a feature screenplay.',
        features: FULL_REPORT_FEATURES,
        badge: 'Most popular',
      },
    ],
  },
  {
    id: 'tv',
    label: 'Television',
    headline: 'Television Coverage',
    blurb:
      'Coverage calibrated for episodic storytelling, from the first pitch through a complete season.',
    scope: 'Pitch docs, pilots, bibles, and full seasons',
    skus: [
      {
        id: 'tv-pitch',
        label: 'TV Pitch',
        productType: 'tv_pitch',
        analysesCount: 1,
        amountCents: 1999,
        description: 'Series concept and pitch-document evaluation.',
        features: [
          'Series concept viability',
          'Pitch structure notes',
          'Audience and market positioning',
        ],
      },
      {
        id: 'tv-series',
        label: 'TV Series',
        productType: 'tv_series',
        analysesCount: 1,
        amountCents: 3999,
        description: 'One clear price for a pilot, pilot plus bible, or full season.',
        features: [
          'Pilot and series arc evaluation',
          'Character and world-building review',
          'Any episode count',
        ],
        badge: 'Most popular',
      },
    ],
  },
  {
    id: 'theater',
    label: 'Theater',
    headline: 'Stage Play Coverage',
    blurb: 'Theater-specific intelligence for production, audience, and market positioning.',
    scope: 'One-acts and full-length plays',
    skus: [
      {
        id: 'stage-play',
        label: 'Stage Play — Theater Intelligence',
        productType: 'stage_play',
        analysesCount: 1,
        amountCents: 2999,
        description: 'Professional analysis calibrated for live performance.',
        features: [
          'Production and staging constraints',
          'Audience and market tier fit',
          'Regional, Off-Broadway, and Broadway positioning',
          'Theater-specific Quilty Score',
        ],
      },
    ],
  },
] as const;

const ALL_SKUS = [
  ...FEATURE_FILM_PACKS,
  ...TV_SERIES_PACKS,
  ...ADDITIONAL_OFFERING_SKUS,
  ...PRICING_CAMPAIGN_SERVICES.flatMap(service => service.skus),
];

export function encodeCampaignSku(sku: CampaignPricingSku): string {
  return `${sku.productType}:${sku.analysesCount}`;
}

export function findCampaignSku(selection: string | null): CampaignPricingSku | undefined {
  if (!selection) return undefined;
  return ALL_SKUS.find(sku => encodeCampaignSku(sku) === selection);
}

const ORDER_TITLES: Record<string, string> = {
  'feature-film-1': 'Feature Film — Full Professional Coverage',
  'feature-film-3': 'Feature Film — 3-Analysis Pack',
  'feature-film-5': 'Feature Film — 5-Analysis Pack',
  'feature-outline': 'Feature Film — Outline',
  'feature-writer': "Feature Film — Writer's Coverage",
  'feature-full': 'Feature Film — Full Professional Coverage',
  'tv-series-1': 'Television — TV Series',
  'tv-series-3': 'Television — 3-Analysis Pack',
  'tv-series-5': 'Television — 5-Analysis Pack',
  'tv-pitch': 'Television — TV Pitch',
  'tv-series': 'Television — TV Series',
  'stage-play': 'Stage Play — Theater Intelligence',
  'novel-adaptation': 'Novel Adaptation',
  'pitch-deck': 'Pitch Deck Analysis',
};

export function campaignOrderTitle(sku: CampaignPricingSku): string {
  return ORDER_TITLES[sku.id] ?? sku.label;
}
